import React from "react";
import { useDispatch, useSelector } from "src/store";

import { addBatchSelectionBulk, removeBatchSelectionBulk, selectBatchActionSelectionTransformed } from "@dashboard/devices/store";
import type { CheckboxProps } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";
import { MinimalTableHeaderCell } from "@shared/table/MinimalTableComponents";
import { Header as TanstackHeader, Table } from "@tanstack/react-table";

interface Props<T = unknown> {
    header: React.PropsWithChildren<TanstackHeader<T, unknown>>;
    id: string;
    table: Table<T>;
}

export const SelectAllLicensesCheckbox = <T,>(props: Props<T>) => {
    const dispatch = useDispatch();
    const selectedItems = useSelector(selectBatchActionSelectionTransformed);

    const visibleRowIds = props.table.getRowModel().rows.map((row) => (row.original as { id: number }).id);
    const selectedIds = Object.keys(selectedItems).map(Number);

    const allSelected = visibleRowIds.length > 0 && visibleRowIds.every((id: number) => selectedIds.includes(id));
    const someSelected = visibleRowIds.some((id: number) => selectedIds.includes(id));

    const onChange: NonNullable<CheckboxProps["onChange"]> = (event) => {
        const checked = event.target.checked;
        const selectedSet = new Set(selectedIds);

        if (checked) {
            const toAdd = visibleRowIds.filter((id) => !selectedSet.has(id));
            if (toAdd.length > 0) {
                dispatch(addBatchSelectionBulk(toAdd));
            }
        } else {
            const toRemove = visibleRowIds.filter((id) => selectedSet.has(id));
            if (toRemove.length > 0) {
                dispatch(removeBatchSelectionBulk(toRemove));
            }
        }
    };

    return (
        <MinimalTableHeaderCell style={{ width: "50px", minWidth: "50px", maxWidth: "50px" }}>
            <div data-column-id={props.id}>
                <CheckboxField checked={allSelected} indeterminate={someSelected && !allSelected} onChange={onChange} />
            </div>
        </MinimalTableHeaderCell>
    );
};
